import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify, useUpdate } from 'react-admin';
import { Building2 } from 'lucide-react';
import { DialogCloseButton } from '../misc/DialogCloseButton';
import { Account } from '../types';

interface AccountEditDialogProps {
    open: boolean;
    onClose: () => void;
    account: Account;
}

export const AccountEditDialog = ({
    open,
    onClose,
    account,
}: AccountEditDialogProps) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [website, setWebsite] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [stateAbbr, setStateAbbr] = useState('');
    const [zipcode, setZipcode] = useState('');

    const [update, { isPending }] = useUpdate();
    const notify = useNotify();

    // Populate fields when dialog opens or account changes
    useEffect(() => {
        if (open && account) {
            setName(account.name || '');
            setPhone(account.phone_number || '');
            setWebsite(account.website || '');
            setAddress(account.address || '');
            setCity(account.city || '');
            setStateAbbr(account.stateAbbr || '');
            setZipcode(account.zipcode || '');
        }
    }, [open, account]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            notify('Account name is required', {
                type: 'warning',
            });
            return;
        }

        try {
            let finalWebsite = website.trim();
            if (finalWebsite && !finalWebsite.startsWith('http')) {
                finalWebsite = `https://${finalWebsite}`;
            }

            const accountData = {
                name: name.trim(),
                phone_number: phone.trim() || null,
                website: finalWebsite || null,
                address: address.trim() || null,
                city: city.trim() || null,
                stateAbbr: stateAbbr.trim() || null,
                zipcode: zipcode.trim() || null,
            };

            await update(
                'accounts',
                {
                    id: account.id,
                    data: accountData,
                    previousData: account,
                },
                { returnPromise: true }
            );

            notify('Account updated successfully', {
                type: 'success',
            });
            onClose();
        } catch {
            notify('Error updating account', {
                type: 'error',
            });
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#cbd5e1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#10b981',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: '#64748b',
            '&.Mui-focused': {
                color: '#10b981',
            },
        },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    },
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                    borderBottom: '1px solid #d1fae5',
                    px: 3,
                    py: 2.5,
                    position: 'relative',
                }}
            >
                <DialogCloseButton onClose={onClose} top={12} right={12} />
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: '#d1fae5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Building2 size={20} color="#059669" />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: '#0f172a',
                            }}
                        >
                            Edit Account
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                color: '#64748b',
                            }}
                        >
                            Update account information
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
                {/* Account Information Section */}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 2,
                    }}
                >
                    Account Information
                </Typography>

                <Stack spacing={2}>
                    {/* Name */}
                    <TextField
                        label="Account Name"
                        required
                        fullWidth
                        size="small"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={fieldSx}
                    />

                    {/* Phone & Website Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Phone Number"
                            fullWidth
                            size="small"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Website"
                            fullWidth
                            size="small"
                            value={website}
                            onChange={e => setWebsite(e.target.value)}
                            placeholder="example.com"
                            sx={fieldSx}
                        />
                    </Stack>
                </Stack>

                {/* Address Section */}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mt: 3,
                        mb: 2,
                    }}
                >
                    Address
                </Typography>

                <Stack spacing={2}>
                    {/* Street Address */}
                    <TextField
                        label="Street Address"
                        fullWidth
                        size="small"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        sx={fieldSx}
                    />

                    {/* City, State, Zip Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="City"
                            fullWidth
                            size="small"
                            value={city}
                            onChange={e => setCity(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="State"
                            size="small"
                            value={stateAbbr}
                            onChange={e => setStateAbbr(e.target.value)}
                            placeholder="MT"
                            sx={{
                                ...fieldSx,
                                minWidth: 90,
                                maxWidth: 90,
                            }}
                        />
                        <TextField
                            label="Zip Code"
                            size="small"
                            value={zipcode}
                            onChange={e => setZipcode(e.target.value)}
                            sx={{
                                ...fieldSx,
                                minWidth: 120,
                                maxWidth: 120,
                            }}
                        />
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid #e2e8f0',
                    gap: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        color: '#64748b',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isPending}
                    startIcon={
                        isPending ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <Building2 size={16} />
                        )
                    }
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#059669',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#86efac',
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};
